import React, { ReactNode, useState } from 'react';
import { Box, ButtonBase, Flex, Stack, Text, Collapse } from '@tonic-ui/react';
import styles from './collapsible-cards.module.css';

interface CardProps {
  id: number;
  heading: ReactNode;
  className?: string;
  icon?: ReactNode;
  body?: ReactNode;
  isExpanded: boolean;
  onClick: (id: number) => void;
}
interface CollapsibleCardsProps {
  cards: {
    heading: ReactNode;
    className?: string;
    icon?: ReactNode;
    body?: ReactNode;
  }[];
}

const CollapseExpandSign = ({ isExpanded }) => {
  return (
    <Box
      className={
        isExpanded
          ? `${styles.collapsible_sign} ${styles.collapsible_sign_expanded}`
          : styles.collapsible_sign
      }
    >
      <div />
      <div />
    </Box>
  );
};

const Card = ({ id, isExpanded, heading, onClick, icon }: CardProps): JSX.Element => (
  <ButtonBase
    whiteSpace="pre-wrap"
    borderRadius="lg"
    cursor="pointer"
    onClick={() => onClick(id)}
    backgroundColor="red:60"
    backgroundImage="linear-gradient(122.46deg, #7B1216 9.29%, #D11A21 88.64%)"
    transition="box-shadow .3s"
    _hover={{
      boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.7)',
    }}
  >
    <Stack
      alignItems="start"
      paddingX="58px"
      paddingY="43px"
      height="333px"
      width={{ sm: '374px', md: '384px' }}
    >
      <Box marginBottom="24px">{icon}</Box>
      <Flex flex="1">
        <Text fontWeight="light" lineHeight="1.6" size="2xl" color="white:primary" textAlign="left">
          {heading}
        </Text>
      </Flex>
      <Box>
        <CollapseExpandSign isExpanded={isExpanded} />
      </Box>
    </Stack>
  </ButtonBase>
);

const CollapsibleCards = ({ cards }: CollapsibleCardsProps): JSX.Element => {
  const [expandedCardId, setExpandedCardId] = useState<number | undefined>();

  const handleOnCardToggle = (id: number) => {
    setExpandedCardId(expandedCardId === id ? undefined : id);
  };

  return (
    <Stack alignItems="center">
      <Box maxWidth="1200px" zIndex="1">
        <Flex
          direction={{ sm: 'column', md: 'row' }}
          gap="24px"
          flexWrap="wrap"
          justifyContent="center"
        >
          {cards.map(({ heading, icon, ...rest }, index) => (
            <Box key={`collapse-card-${index}`} maxWidth={{ sm: '374px', md: '384px' }}>
              <Card
                heading={heading}
                id={index}
                icon={icon}
                isExpanded={expandedCardId === index}
                onClick={handleOnCardToggle}
                {...rest}
              />
              <Collapse in={expandedCardId === index} className={styles.mobile}>
                <Stack>{cards[expandedCardId as number]?.body}</Stack>
              </Collapse>
            </Box>
          ))}
        </Flex>
        <Collapse in={expandedCardId !== undefined} className={styles.large}>
          <Stack paddingTop="48px">{cards[expandedCardId as number]?.body}</Stack>
        </Collapse>
      </Box>
    </Stack>
  );
};

CollapsibleCards.displayName = 'CollapsibleCards';

export default CollapsibleCards;
